import { appState } from '../../../interfaces/common';
import {
  Button,
  Flex,
  Form,
  Grid,
  Input,
  Option,
  RadioButton,
  RadioButtonGroup,
  Select,
  Text,
  TextArea,
} from '@pega/cosmos-react-core';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AzureModels, OpenAIHost, OpenAIModels, VerticalTemplateOption } from '../../../constants/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import CustomLabel from '../../Utilities/CustomLabel';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { addNewAISimulatorConnection, getAzureModels } from './AISimulatorApis';

interface AISimulatorFormData {
  accountName: string;
  apiKey: string;
  host?: OpenAIHost;
  clientId?: string;
  azureResource?: string;
  selectedModel?: string;
  apiVersion?: string;
  oauthEndpoint?: string;
}

const CreateCustomerAISimulator: React.FC = () => {
  const { register, handleSubmit, errors, reset, formState } = useForm<AISimulatorFormData>({ mode: 'all' });
  const { sessionId, genAiKeyOption } = useSelector((state: appState) => state);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  /**
   * Setting the default host as OpenAI on initial reader of create connection
   */
  const [selectedHost, setSelectedHost] = useState(OpenAIHost.OpenAI);
  const [selectedTemplate, setSelectedTemplate] = useState(VerticalTemplateOption.Default);
  const completeHost = ['no', 'yes'];
  const [completeHostDetails, setCompleteHostDetails] = useState(completeHost[0]);
  const [azureModels, setAzureModels] = useState(AzureModels);

  const getModels = async (data: AISimulatorFormData) => {
    const validPayload = data.apiKey && data.apiVersion && data.azureResource && data.clientId && data.oauthEndpoint;
    if (validPayload) {
      dispatch(updateLoaderDetails(true));
      const response = await getAzureModels(sessionId, data);
      dispatch(updateLoaderDetails(false));
      if (response?.status === 200 && response?.data?.length > 0) {
        setAzureModels(response?.data);
      } else {
        dispatch(updateBannerInfo(translate('get_models_error'), 'info'));
      }
    }
  };

  const onSubmit = async (data: AISimulatorFormData) => {
    const payload = {
      ...data,
      isCustomerSimulator: true,
      host: completeHostDetails === completeHost[1] ? (selectedHost as OpenAIHost) : undefined,
      selectedVerticalOption: selectedTemplate as VerticalTemplateOption,
      azureModels: azureModels,
    };

    if (formState.touched) {
      dispatch(updateLoaderDetails(true));
      const json = await addNewAISimulatorConnection(sessionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/custom/${json?.data?.accountId}`);
        dispatch(updateBannerInfo(`${translate('GenAI Agent Trainer')} ${translate('account_registered')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        dispatch(updateBannerInfo(errorMessage || translate('agent_trainer_creation_failure'), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const headerElement = () => {
    return <Text variant="h1">{`${translate('new')} ${translate('GenAI Agent Trainer')}`}</Text>;
  };

  const updateSelectedTemplate = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTemplate(e.target.value as VerticalTemplateOption);
  };

  const updateCompleteHostDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompleteHostDetails(e.target.value);
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      <Grid
        container={{
          cols: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
          <Input
            name="accountName"
            label={translate('name')}
            required={true}
            autoComplete="off"
            info={errors.accountName?.message}
            status={errors.accountName?.message ? 'error' : undefined}
            ref={register({ required: translate('name_empty') })}
          />
          <Select
            label={translate('template')}
            name="templateVertical"
            value={selectedTemplate}
            onChange={updateSelectedTemplate}
          >
            {Object.values(VerticalTemplateOption).map((item) => (
              <Option value={item} key={item}>
                {translate(item)}
              </Option>
            ))}
          </Select>
          {genAiKeyOption !== 'pega' && (
            <RadioButtonGroup label={translate('genAI_host_option')} onChange={updateCompleteHostDetails}>
              {completeHost.map((item, index) => {
                return (
                  <RadioButton
                    key={index}
                    label={translate(item)}
                    value={item}
                    checked={item === completeHostDetails}
                  />
                );
              })}
            </RadioButtonGroup>
          )}
          {completeHostDetails === completeHost[1] && genAiKeyOption !== 'pega' && (
            <>
              <Flex
                container={{
                  direction: 'column',
                  gap: 1,
                }}
              >
                <CustomLabel label={translate('host')} showRequired={false} tooltipInfo={translate('host_tooltip')} />
                <Select
                  name="host"
                  value={selectedHost}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setSelectedHost(e.target.value as OpenAIHost);
                  }}
                >
                  <Option value={OpenAIHost.OpenAI}>{translate('OpenAI')}</Option>
                  <Option value={OpenAIHost.Azure}>{translate('Azure')}</Option>
                  <Option value={OpenAIHost.Pega}>{translate('Pega GenAI')}</Option>
                </Select>
              </Flex>

              {selectedHost === OpenAIHost.OpenAI && (
                <Select name="selectedModel" label={translate('selected_model')} ref={register()}>
                  {OpenAIModels.map((item) => (
                    <Option value={item.id} key={item.id}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              )}

              {selectedHost === OpenAIHost.Azure && (
                <>
                  <Flex
                    container={{
                      direction: 'column',
                      gap: 1,
                    }}
                  >
                    <CustomLabel
                      label={translate('resource')}
                      showRequired={false}
                      tooltipInfo={translate('resource_tooltip')}
                    />
                    <Input
                      name="azureResource"
                      label={translate('resource')}
                      labelHidden={true}
                      autoComplete="off"
                      type="text"
                      ref={register()}
                    />
                  </Flex>

                  <Input name="oauthEndpoint" label={translate('oauth_endpoint')} ref={register()} />
                  <Flex
                    container={{
                      direction: 'column',
                      gap: 1,
                    }}
                  >
                    <CustomLabel
                      label={translate('api_version')}
                      showRequired={false}
                      tooltipInfo={translate('api_version_tooltip')}
                    />
                    <Input
                      name="apiVersion"
                      label={translate('api_version')}
                      labelHidden={true}
                      autoComplete="off"
                      type="text"
                      ref={register()}
                    />
                  </Flex>
                  <Flex
                    container={{
                      direction: 'column',
                      gap: 1,
                    }}
                  >
                    <CustomLabel
                      label={translate('client_id')}
                      showRequired={false}
                      tooltipInfo={translate('client_id_tooltip')}
                    />
                    <TextArea
                      name="clientId"
                      label={translate('client_id')}
                      labelHidden={true}
                      autoComplete="off"
                      type="text"
                      style={{ minHeight: 100 }}
                      ref={register()}
                    />
                  </Flex>
                </>
              )}
              {selectedHost !== OpenAIHost.Pega && (
                <Flex
                  container={{
                    direction: 'column',
                    gap: 1,
                  }}
                >
                  <CustomLabel
                    label={translate(selectedHost === OpenAIHost.Azure ? 'client_secret' : 'api_key')}
                    showRequired={false}
                    tooltipInfo={translate(
                      selectedHost === OpenAIHost.Azure ? 'client_secret_tooltip' : 'api_key_tooltip',
                    )}
                  />
                  <TextArea
                    name="apiKey"
                    label={translate(selectedHost === OpenAIHost.Azure ? 'client_secret' : 'api_key')}
                    labelHidden={true}
                    autoComplete="off"
                    type="text"
                    style={{ minHeight: 100 }}
                    ref={register()}
                  />
                </Flex>
              )}
              {selectedHost === OpenAIHost.Azure && (
                <Flex
                  container={{
                    direction: 'row',
                    alignItems: 'start',
                    itemGap: 1,
                  }}
                >
                  <Flex item={{ grow: 1 }}>
                    <Select name="selectedModel" label={translate('selected_model')} ref={register()}>
                      {azureModels.map((item: any) => (
                        <Option value={item.id ? item.id : item} key={item.id ? item.id : item}>
                          {item.label ? item.label : item}
                        </Option>
                      ))}
                    </Select>
                  </Flex>
                  <Flex container style={{ paddingTop: '20px' }}>
                    <Button onClick={handleSubmit(getModels)} variant="primary">
                      {translate('get_models')}
                    </Button>
                  </Flex>
                </Flex>
              )}
            </>
          )}
        </Form>
      </Grid>
    </SectionRenderTemplate>
  );
};

export default CreateCustomerAISimulator;
